import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  Box,
  Paper,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  TextField,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  SafetyDivider as SafetyDividerIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import TagInput from '../../../../Components/MUIs/TagInput';
import WorkTypeSelectPopup from './WorkTypeSelectPopup';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import {
  useSearchWorkTypeItems,
  useSearchWorkTypeCodes,
  useSearchWorkTypeCustMutation,
} from './SAM_BASIC_02_WorkType_QUERY';
import {
  MapStandardWorkTypeRows,
  MapCustWorkTypeRows,
  BuildWorkTypeCodeMap,
} from './SAM_BASIC_02_WorkTypeUtils';

/** 항목관리 팝업 로드 시 서버에 없으면 자동 세팅할 작업종류(codeNm 기준, 표시 순서) */
const DEFAULT_WORK_TYPES_ORDERED = ['일반작업', '화기작업'];

function MergeDefaultWorkTypeRows(finalResult, codeRows, workTypeCodeMap) {
  const list = Array.isArray(finalResult) ? [...finalResult] : [];
  const existing = new Set(
    list.map((r) => String(r.workTypeCd ?? '').trim()).filter(Boolean)
  );
  const master = Array.isArray(codeRows)
    ? codeRows.filter((item) => item.groupCd === 'WORK_TYPE')
    : [];
  let ts = Date.now();
  for (const nm of DEFAULT_WORK_TYPES_ORDERED) {
    const row = master.find((c) => String(c.codeNm ?? '').trim() === nm);
    if (!row?.code) continue;
    const cd = String(row.code).trim();
    if (!cd || existing.has(cd)) continue;
    existing.add(cd);
    list.push({
      id: `workType_${cd}_${ts}`,
      workItemId: '',
      workTypeCd: cd,
      workTypeNm: row.codeNm || workTypeCodeMap[cd] || cd,
      hazardousItems: [],
      hazardousItemCodes: [],
      hazardousItemMap: [],
      safetyEquipment: [],
      safetyEquipmentCodes: [],
      safetyEquipmentMap: [],
      preparations: [''],
      preparationCodes: [],
      preparationMap: [],
    });
    ts += 1;
  }
  return list;
}

/**
 * 작업 종류별 항목 관리 팝업
 * @param {Boolean} open - 팝업 열림 여부
 * @param {Function} onClose - 닫기 콜백
 * @param {Function} onSave - 저장 콜백 (데이터 전달)
 * @param {String} custCd - 사업장 코드
 */
function WorkTypeItemManagement({ open, onClose, onSave, custCd, userInfo = {} }) {
  const dispatch = useDispatch();
  const [workTypeItems, setWorkTypeItems] = useState([]);
  const [workTypeSelectOpen, setWorkTypeSelectOpen] = useState(false);
  // 초기 로드된 데이터 저장 (삭제 비교용)
  const [initialWorkTypeItems, setInitialWorkTypeItems] = useState([]);

  // context를 useMemo로 동적으로 계산
  const context = useMemo(
    () => ({
      search: {
        custCd: custCd || '',
      },
    }),
    [custCd]
  );

  const { runAsync: SearchWorkTypeCodesAsync } = useSearchWorkTypeCodes();
  const { runAsync: SearchWorkTypeCustAsync } = useSearchWorkTypeCustMutation();

  // 작업종류별 항목 조회 훅
  const { refetch: SearchWorkTypeItems } = useSearchWorkTypeItems({
    context: context,
    onSuccess: async (rows) => {
      // 작업종류 코드 조회
      let custRows = [];
      if (custCd?.trim()) {
        await SearchWorkTypeCustAsync(
          { search: { custCd } },
          {
            onSuccess: (rows) => {
              custRows = rows || [];
            },
          }
        );
      }

      await SearchWorkTypeCodesAsync(null, {
        onSuccess: (codeRows) => {
          const standard = MapStandardWorkTypeRows(codeRows);
          const cust = MapCustWorkTypeRows(custRows);
          const workTypeCodeMap = BuildWorkTypeCodeMap(standard, cust);

          // 데이터 변환 (쿼리에서 이미 중복 제거 및 집계됨)
          // 서버 응답: PascalCase (WorkType, RiskNmList, RiskCodeList, WorkEquipNmList, WorkEquipCodeList, PreparationNmList, PreparationCodeList)
          const finalResult = rows.map((row, index) => {
            const workType = row.WorkType || row.workType || row.WORK_TYPE || '';
            const workItemId = row.WorkItemId || row.workItemId || row.WORK_ITEM_ID || '';

            // 위험사항 파싱: 코드와 이름을 함께 조회하므로 코드를 키로 사용
            const riskNmList = row.RiskNmList || row.riskNmList || row.RISK_NM_LIST || '';
            const riskCodeList = row.RiskCodeList || row.riskCodeList || row.RISK_CODE_LIST || '';
            
            // 위험사항 이름 배열 (표시용)
            const hazardousItems = riskNmList
              ? riskNmList.split('|||').filter((item) => item && item.trim()).map((item) => item.trim())
              : [];
            
            // 위험사항 코드 배열 (표시용)
            const riskCodes = riskCodeList
              ? riskCodeList.split('|||').filter((item) => item && item.trim()).map((item) => String(item).trim())
              : [];

            // 위험사항 매핑: 서버 쿼리에서 이름과 코드가 같은 순서로 정렬됨 (ORDER BY TBR.RISK_CODE ASC)
            // 인덱스 매칭이 정확하게 동작합니다.
            const hazardousItemMap = [];
            
            // 코드와 이름을 같은 인덱스로 매칭
            const minHazardousLength = Math.min(hazardousItems.length, riskCodes.length);
            for (let idx = 0; idx < minHazardousLength; idx++) {
              const name = hazardousItems[idx]?.trim() || '';
              const code = riskCodes[idx]?.trim() || null;
              
              if (name && code) {
                hazardousItemMap.push({
                  name: name,
                  code: code,
                });
              }
            }
            
            // 이름 배열에만 있는 항목 처리 (코드가 없는 경우)
            for (let idx = minHazardousLength; idx < hazardousItems.length; idx++) {
              const name = hazardousItems[idx]?.trim() || '';
              if (name) {
                hazardousItemMap.push({
                  name: name,
                  code: null,
                });
              }
            }

            // 안전보호구 파싱: 코드와 이름을 함께 조회하므로 코드를 키로 사용
            const workEquipNmList = row.WorkEquipNmList || row.workEquipNmList || row.WORK_EQUIP_NM_LIST || '';
            const workEquipCodeList = row.WorkEquipCodeList || row.workEquipCodeList || row.WORK_EQUIP_CODE_LIST || '';
            
            // 안전보호구 이름 배열 (표시용)
            const safetyEquipment = workEquipNmList
              ? workEquipNmList.split('|||').filter((item) => item && item.trim()).map((item) => item.trim())
              : [];
            
            // 안전보호구 코드 배열 (표시용)
            const equipCodes = workEquipCodeList
              ? workEquipCodeList.split('|||').filter((item) => item && item.trim()).map((item) => String(item).trim())
              : [];

            // 안전보호구 매핑: 서버 쿼리에서 이름과 코드가 같은 순서로 정렬됨 (ORDER BY TBE.WORK_EQUIP_CODE ASC)
            // 인덱스 매칭이 정확하게 동작합니다.
            const safetyEquipmentMap = [];
            
            // 코드와 이름을 같은 인덱스로 매칭
            const minEquipLength = Math.min(safetyEquipment.length, equipCodes.length);
            for (let idx = 0; idx < minEquipLength; idx++) {
              const name = safetyEquipment[idx]?.trim() || '';
              const code = equipCodes[idx]?.trim() || null;
              
              if (name && code) {
                safetyEquipmentMap.push({
                  name: name,
                  code: code,
                });
              }
            }
            
            // 이름 배열에만 있는 항목 처리 (코드가 없는 경우)
            for (let idx = minEquipLength; idx < safetyEquipment.length; idx++) {
              const name = safetyEquipment[idx]?.trim() || '';
              if (name) {
                safetyEquipmentMap.push({
                  name: name,
                  code: null,
                });
              }
            }

            // 준비사항 파싱 (||| 구분자로 분리)
            const preparationNmList = row.PreparationNmList || row.preparationNmList || row.PREPARATION_NM_LIST || '';
            const preparationNames = preparationNmList
              ? preparationNmList.split('|||').filter(
                  (item) => item && item.trim()
                )
              : [];

            // 준비사항 코드 파싱 (||| 구분자로 분리)
            const preparationCodeList = row.PreparationCodeList || row.preparationCodeList || row.PREPARATION_CODE_LIST || '';
            const preparationCodes = preparationCodeList
              ? preparationCodeList.split('|||').filter(
                  (item) => item && item.trim()
                )
              : [];

            // 준비사항 매핑: 서버 쿼리에서 이름과 코드가 같은 순서로 정렬됨
            // 인덱스 매칭이 정확하게 동작합니다.
            const preparationMap = [];
            
            // 코드와 이름을 같은 인덱스로 매칭
            const minPrepLength = Math.min(preparationNames.length, preparationCodes.length);
            for (let idx = 0; idx < minPrepLength; idx++) {
              const name = preparationNames[idx]?.trim() || '';
              const code = preparationCodes[idx]?.trim() || null;
              
              if (name && code) {
                preparationMap.push({
                  name: name,
                  code: code,
                });
              }
            }
            
            // 이름 배열에만 있는 항목 처리 (코드가 없는 경우)
            for (let idx = minPrepLength; idx < preparationNames.length; idx++) {
              const name = preparationNames[idx]?.trim() || '';
              if (name) {
                preparationMap.push({
                  name: name,
                  code: null,
                });
              }
            }

            // 준비사항 이름 배열 (표시용)
            const preparations = preparationNames.length > 0 ? preparationNames : [''];

            return {
              id: `workType_${workType}_${Date.now()}_${index}`,
              workItemId: workItemId, // 저장 시 필요
              workTypeCd: workType,
              workTypeNm: workTypeCodeMap[workType] || workType,
              hazardousItems: hazardousItems,
              hazardousItemCodes: riskCodes, // 코드 리스트 저장 (호환성 유지)
              hazardousItemMap: hazardousItemMap, // 이름-코드 매핑 저장
              safetyEquipment: safetyEquipment,
              safetyEquipmentCodes: equipCodes, // 코드 리스트 저장 (호환성 유지)
              safetyEquipmentMap: safetyEquipmentMap, // 이름-코드 매핑 저장
              preparations: preparations,
              preparationCodes: preparationCodes, // 코드 리스트 저장 (호환성 유지)
              preparationMap: preparationMap, // 이름-코드 매핑 저장
            };
          });

          const withDefaults = MergeDefaultWorkTypeRows(
            finalResult,
            codeRows,
            workTypeCodeMap
          );
          setWorkTypeItems(withDefaults);
          // 초기 데이터 저장 (삭제 비교용)
          setInitialWorkTypeItems(JSON.parse(JSON.stringify(withDefaults)));
        },
      });
    },
  });

  // 작업종류별 항목 조회 함수
  const loadWorkTypeItems = React.useCallback(async () => {
    try {
      await SearchWorkTypeItems();
    } catch (error) {
      // 에러는 onError에서 처리
      dispatch(
        showSnackbar({
          message: error.message || '조회 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
      setWorkTypeItems([]);
    }
  }, [SearchWorkTypeItems, dispatch]);

  // 팝업이 열릴 때 데이터 조회
  useEffect(() => {
    if (open && custCd) {
      loadWorkTypeItems();
    } else if (open && !custCd) {
      setWorkTypeItems([]);
    }
  }, [open, custCd, loadWorkTypeItems]);

  // 작업 종류 추가 (선택 팝업 확인)
  const handleAddWorkType = (selectedWorkTypes) => {
    const ts = Date.now();
    const newItems = selectedWorkTypes.map((workType, idx) => ({
      id: `workType_${workType.code}_${ts}_${idx}`,
      workTypeCd: workType.code,
      workTypeNm: workType.codeNm,
      hazardousItems: [],
      safetyEquipment: [],
      preparations: [''],
    }));

    const existingCodes = workTypeItems.map((item) => item.workTypeCd);
    const filteredNewItems = newItems.filter(
      (item) => !existingCodes.includes(item.workTypeCd)
    );

    if (filteredNewItems.length === 0) {
      dispatch(
        showSnackbar({
          message: '선택한 작업종류는 이미 항목 관리에 있습니다.',
          type: 'info',
        })
      );
      return;
    }

    setWorkTypeItems((prev) => [...prev, ...filteredNewItems]);
    dispatch(
      showSnackbar({
        message: `작업종류 ${filteredNewItems.length}건이 추가되었습니다.`,
        type: 'success',
      })
    );
  };

  // 작업 종류 삭제
  const handleDeleteWorkType = (id) => {
    setWorkTypeItems((prev) => prev.filter((item) => item.id !== id));
  };

  // 위험사항 변경 (이름 배열 변경 시 코드 배열도 동기화)
  const handleHazardousItemsChange = (id, items) => {
    setWorkTypeItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const currentItems = item.hazardousItems || [];
        const itemMap = item.hazardousItemMap || [];

        // 이전 항목과 비교하여 삭제된 항목 찾기
        const deletedItems = currentItems.filter(
          (currentItem) => !items.some((item) => item === currentItem)
        );

        // 삭제된 항목의 코드도 매핑에서 제거
        const updatedMap = itemMap.filter(
          (mapped) => !deletedItems.includes(mapped.name)
        );

        // 새로운 항목 추가 (매핑에 없는 경우)
        items.forEach((name) => {
          const existing = updatedMap.find((m) => m.name === name);
          if (!existing) {
            updatedMap.push({ name, code: null });
          }
        });

        // 코드 배열 업데이트 (매핑에서 코드가 있는 항목만)
        const updatedCodes = updatedMap
          .map((mapped) => mapped.code)
          .filter((code) => code != null);

        return {
          ...item,
          hazardousItems: items,
          hazardousItemCodes: updatedCodes,
          hazardousItemMap: updatedMap,
        };
      })
    );
  };

  // 안전보호구 변경 (이름 배열 변경 시 코드 배열도 동기화)
  const handleSafetyEquipmentChange = (id, items) => {
    setWorkTypeItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const currentItems = item.safetyEquipment || [];
        const itemMap = item.safetyEquipmentMap || [];

        // 이전 항목과 비교하여 삭제된 항목 찾기
        const deletedItems = currentItems.filter(
          (currentItem) => !items.some((item) => item === currentItem)
        );

        // 삭제된 항목의 코드도 매핑에서 제거
        const updatedMap = itemMap.filter(
          (mapped) => !deletedItems.includes(mapped.name)
        );

        // 새로운 항목 추가 (매핑에 없는 경우)
        items.forEach((name) => {
          const existing = updatedMap.find((m) => m.name === name);
          if (!existing) {
            updatedMap.push({ name, code: null });
          }
        });

        // 코드 배열 업데이트 (매핑에서 코드가 있는 항목만)
        const updatedCodes = updatedMap
          .map((mapped) => mapped.code)
          .filter((code) => code != null);

        return {
          ...item,
          safetyEquipment: items,
          safetyEquipmentCodes: updatedCodes,
          safetyEquipmentMap: updatedMap,
        };
      })
    );
  };

  // 준비사항 추가
  const handleAddPreparation = (id) => {
    setWorkTypeItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, preparations: [...item.preparations, ''] }
          : item
      )
    );
  };

  // 안전조치 사항 변경 (입력값은 trim 없이 저장 — 띄어쓰기 유지, 코드 매핑만 동기화)
  const handlePreparationChange = (id, index, value) => {
    setWorkTypeItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const previousRaw = item.preparations[index] ?? '';
        const updatedPreparations = item.preparations.map((prep, i) =>
          i === index ? value : prep
        );

        const currentMap = item.preparationMap || [];
        const matchesMapEntry = (m) => {
          if (!m || m.name == null) return false;
          return (
            m.name === previousRaw ||
            String(m.name).trim() === String(previousRaw).trim()
          );
        };

        const mapIndex = currentMap.findIndex(matchesMapEntry);
        let updatedMap = [...currentMap];

        if (mapIndex >= 0) {
          updatedMap[mapIndex] = { ...updatedMap[mapIndex], name: value };
        } else if (String(value).trim()) {
          updatedMap.push({ name: value, code: null });
        }

        const updatedCodes = updatedMap
          .map((mapped) => mapped.code)
          .filter((code) => code != null);

        return {
          ...item,
          preparations: updatedPreparations,
          preparationCodes: updatedCodes,
          preparationMap: updatedMap,
        };
      })
    );
  };

  // 준비사항 삭제 (이름 배열 변경 시 코드 배열도 동기화)
  const handleDeletePreparation = (id, index) => {
    setWorkTypeItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        // 삭제할 준비사항의 이름 추출 (preparations는 이름 배열)
        const deletedName = item.preparations[index] 
          ? item.preparations[index].trim() 
          : null;

        if (!deletedName) return item; // 삭제할 항목이 없으면 그대로 반환

        // 준비사항 배열에서 제거
        const updatedPreparations = item.preparations.filter((_, i) => i !== index);

        const currentMap = item.preparationMap || [];

        // 삭제된 항목의 코드도 매핑에서 제거 (trim()으로 정확히 비교)
        const deletedMapItem = currentMap.find((mapped) => {
          if (!mapped || !mapped.name) return false;
          return mapped.name.trim() === deletedName;
        });

        const updatedMap = currentMap.filter((mapped) => {
          if (!mapped || !mapped.name) return true; // 매핑이 없으면 유지
          return mapped.name.trim() !== deletedName;
        });

        // 코드 배열 업데이트 (매핑에서 코드가 있는 항목만)
        const updatedCodes = updatedMap
          .map((mapped) => mapped.code)
          .filter((code) => code != null);

        // 디버깅: 삭제 확인
        if (deletedMapItem) {
          
        }

        return {
          ...item,
          preparations: updatedPreparations,
          preparationCodes: updatedCodes,
          preparationMap: updatedMap,
        };
      })
    );
  };

  // 저장
  const handleSave = () => {
    // 삭제된 항목을 포함한 전체 데이터 전달
    // 초기 데이터와 비교하여 삭제된 작업종류 및 항목도 함께 전달
    const saveData = {
      current: workTypeItems,
      initial: initialWorkTypeItems,
    };
    onSave?.(saveData);
    onClose();
  };

  // 팝업 닫을 때 초기화
  const handleClose = () => {
    setWorkTypeItems([]);
    onClose();
  };

  return (
    <>
      <WorkTypeSelectPopup
        open={workTypeSelectOpen}
        onClose={() => setWorkTypeSelectOpen(false)}
        onSelect={handleAddWorkType}
        custCd={custCd}
        userInfo={userInfo}
        selectedWorkTypes={workTypeItems
          .filter((item) => item.workTypeCd)
          .map((item) => ({ code: item.workTypeCd, codeNm: item.workTypeNm }))}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        contentSx={{
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden',
          maxHeight: '90vh',
        }}
        PaperProps={{
          sx: {
            borderRadius: 0,
            overflow: 'hidden',
            maxHeight: '90vh',
            height: 'auto',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            bgcolor: '#f6f7f8',
          }}
        >
          {/* Top Navigation Bar */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              whiteSpace: 'nowrap',
              borderBottom: '1px solid #f0f2f4',
              bgcolor: 'white',
              px: 3,
              py: 1.5,
              flexShrink: 0,
              zIndex: 20,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                color: '#111418',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(19, 127, 236, 0.1)',
                  color: 'primary.main',
                  borderRadius: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '20px',
                  }}
                >
                  ⚙️
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#111418',
                    fontSize: 22,
                    fontWeight: 'bold',
                    letterSpacing: -0.15,
                    mb: 0.5,
                  }}
                >
                  작업 종류별 항목 관리
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#617589',
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                >
                  작업 종류에 따른 유해위험요인, 안전보호구, 안전조치 사항을 관리합니다
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setWorkTypeSelectOpen(true)}
                sx={{
                  color: '#ffffff',
                  fontSize: 14,
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  backgroundColor: '#1976d2',
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                작업종류 추가
              </Button>
              <IconButton
                onClick={handleClose}
                size="small"
                sx={{
                  color: '#617589',
                  '&:hover': {
                    color: '#111418',
                    bgcolor: 'transparent',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Paper>

          {/* 컨텐츠 영역 */}
          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              maxHeight: 'calc(90vh - 180px)',
              p: 2,
            }}
          >
            {/* 아코디언 리스트 */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: 0,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                },
              }}
            >
              {workTypeItems.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: '#617589',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.875rem',
                    }}
                  >
                    작업 종류를 추가해주세요.
                  </Typography>
                </Box>
              ) : (
                workTypeItems.map((item, index) => (
                  <Accordion
                    key={item.id}
                    defaultExpanded={index === 0}
                    sx={{
                      mb: 3,
                      borderRadius: 1,
                      border: '1px solid #dbe0e6',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                      backgroundColor: 'white',
                      '&:before': {
                        display: 'none',
                      },
                      '&.Mui-expanded': {
                        margin: '0 0 24px 0',
                        backgroundColor: 'white',
                        border: '1px solid #dbe0e6',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{
                            color: '#617589',
                            fontSize: '24px',
                          }}
                        />
                      }
                      aria-controls={`panel-${item.id}-content`}
                      id={`panel-${item.id}-header`}
                      sx={{
                        bgcolor: 'white',
                        borderRadius: '4px 4px 0 0',
                        minHeight: 56,
                        px: 2.5,
                        borderBottom: '1px solid #e5e7eb',
                        '&.Mui-expanded': {
                          minHeight: 56,
                          borderBottom: '2px solid #dbe0e6',
                        },
                        '&:hover': {
                          bgcolor: '#f9fafb',
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0',
                          alignItems: 'center',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          pr: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '6px',
                              backgroundColor: 'rgba(19, 127, 236, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: 'primary.main',
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: '#111418',
                              fontWeight: 600,
                              fontSize: '0.9375rem',
                            }}
                          >
                            {item.workTypeNm}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteWorkType(item.id);
                          }}
                          sx={{
                            color: '#617589',
                            '&:hover': {
                              backgroundColor: '#f3f4f6',
                              color: '#111418',
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        px: 2.5,
                        py: 3,
                        backgroundColor: '#f9fafb',
                      }}
                    >
                      <Stack spacing={3}>
                        {/* 위험사항 */}
                        <Box
                          sx={{
                            backgroundColor: 'white',
                            borderRadius: 1,
                            padding: 2.5,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              mb: 2,
                              pb: 1.5,
                              borderBottom: '2px solid #f0f2f4',
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '6px',
                                bgcolor: 'rgba(244, 67, 54, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#f44336',
                              }}
                            >
                              <WarningIcon
                                sx={{
                                  fontSize: '20px',
                                }}
                              />
                            </Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: '#111418',
                                fontSize: '0.875rem',
                              }}
                            >
                              유해위험요인    
                            </Typography>
                          </Box>
                          <TagInput
                            value={item.hazardousItems}
                            onChange={(items) =>
                              handleHazardousItemsChange(item.id, items)
                            }
                            placeholder="유해위험요인을 입력하세요 (예: 가연성, 독성, 고압 등)"
                          />
                        </Box>

                        {/* 안전보호구 */}
                        <Box
                          sx={{
                            backgroundColor: 'white',
                            borderRadius: 1,
                            padding: 2.5,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              mb: 2,
                              pb: 1.5,
                              borderBottom: '2px solid #f0f2f4',
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '6px',
                                bgcolor: 'rgba(25, 118, 210, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#1976d2',
                              }}
                            >
                              <SafetyDividerIcon
                                sx={{
                                  fontSize: '20px',
                                }}
                              />
                            </Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: '#111418',
                                fontSize: '0.875rem',
                              }}
                            >
                              안전보호구
                            </Typography>
                          </Box>
                          <TagInput
                            value={item.safetyEquipment}
                            onChange={(items) =>
                              handleSafetyEquipmentChange(item.id, items)
                            }
                            placeholder="안전보호구를 입력하세요 (예: 안전모, 안전화, 장갑 등)"
                          />
                        </Box>

                        {/* 준비사항 */}
                        <Box
                          sx={{
                            backgroundColor: 'white',
                            borderRadius: 1,
                            padding: 2.5,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2,
                              pb: 1.5,
                              borderBottom: '2px solid #f0f2f4',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '6px',
                                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#4caf50',
                                }}
                              >
                                <CheckCircleIcon
                                  sx={{
                                    fontSize: '20px',
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: '#111418',
                                  fontSize: '0.875rem',
                                }}
                              >
                                안전조치 사항
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleAddPreparation(item.id)}
                              sx={{
                                color: '#617589',
                                '&:hover': {
                                  backgroundColor: '#f3f4f6',
                                  color: '#111418',
                                },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Stack spacing={1.5}>
                            {item.preparations.map((prep, prepIndex) => (
                              <Box
                                key={prepIndex}
                                sx={{
                                  display: 'flex',
                                  gap: 1.5,
                                  alignItems: 'flex-start',
                                  backgroundColor: '#ffffff',
                                  borderRadius: 1,
                                  padding: 1.5,
                                  border: '1px solid #f0f2f4',
                                  '&:hover': {
                                    borderColor: '#dbe0e6',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    minWidth: 24,
                                    height: 24,
                                    borderRadius: '4px',
                                    bgcolor: 'rgba(19, 127, 236, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    mt: 0.5,
                                  }}
                                >
                                  {prepIndex + 1}
                                </Box>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={2}
                                  value={prep}
                                  onChange={(e) => {
                                    // 이름만 저장 (코드는 preparationMap에서 관리)
                                    handlePreparationChange(
                                      item.id,
                                      prepIndex,
                                      e.target.value
                                    );
                                  }}
                                  placeholder="안전조치 사항을 입력하세요"
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: '#ffffff',
                                      '& fieldset': {
                                        borderColor: '#e5e7eb',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#d1d5db',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                      },
                                    },
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDeletePreparation(
                                      item.id,
                                      prepIndex
                                    )
                                  }
                                  sx={{
                                    mt: 0.5,
                                    color: '#617589',
                                    '&:hover': {
                                      backgroundColor: '#f3f4f6',
                                      color: '#111418',
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>

            {/* 하단 버튼 */}
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
                px: 3,
                py: 2,
                borderTop: '1px solid #f0f2f4',
                bgcolor: 'white',
                flexShrink: 0,
              }}
            >
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  minWidth: '100px',
                  color: '#617589',
                  borderColor: '#e5e7eb',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: '#d1d5db',
                    bgcolor: 'transparent',
                  },
                }}
              >
                취소
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                sx={{
                  minWidth: '100px',
                  fontWeight: 500,
                }}
              >
                저장
              </Button>
            </Paper>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

export default WorkTypeItemManagement;
